import { addDays, addMonths, addYears, parseISO, formatISO } from 'date-fns';
import type { RecurrenceType } from '../types';

/**
 * Given the due date that just occurred and the obligation's recurrence rule,
 * compute the next due date. Returns null for one-time ("none") obligations --
 * those don't get a next occurrence, they just retire.
 */
export function computeNextDueDate(
  currentDueDateISO: string,
  recurrenceType: RecurrenceType,
  recurrenceInterval: number | null
): string | null {
  const current = parseISO(currentDueDateISO);

  switch (recurrenceType) {
    case 'none':
      return null;
    case 'daily':
      return toDateOnly(addDays(current, 1));
    case 'weekly':
      return toDateOnly(addDays(current, 7));
    case 'monthly':
      return toDateOnly(addMonths(current, 1));
    case 'quarterly':
      return toDateOnly(addMonths(current, 3));
    case 'semiannually':
      return toDateOnly(addMonths(current, 6));
    case 'annually':
      return toDateOnly(addYears(current, 1));
    case 'every_x_months':
      return toDateOnly(addMonths(current, Math.max(1, recurrenceInterval ?? 1)));
    case 'every_x_years':
      return toDateOnly(addYears(current, Math.max(1, recurrenceInterval ?? 1)));
    default:
      return null;
  }
}

function toDateOnly(d: Date): string {
  return formatISO(d, { representation: 'date' });
}

export function isRecurring(recurrenceType: RecurrenceType): boolean {
  return recurrenceType !== 'none';
}
